import { useCallback } from 'react';
import _ from 'lodash';
import { Button } from '@seeqdev/qomponents';
import { useTranslation } from 'react-i18next';
import { setAIAssistantDisplayed, setAIAssistantExternalPrompt } from '@/aiAssistant/aiAssistant.actions';
import type { FormulaAssistantVariant } from '@/aiAssistant/aiAssistant.types';
import type { FormulaErrorInterface } from '@/formula/formula.types';

interface BaseProps {
  formula: string;
  variant: FormulaAssistantVariant;
}

interface DebugProps extends BaseProps {
  variant: 'debug';
  error: FormulaErrorInterface;
}

interface ExplainProps extends BaseProps {
  variant: 'explain';
}

type FormulaAiAssistantProps = DebugProps | ExplainProps;

export const FormulaToolAiButton: React.FunctionComponent<FormulaAiAssistantProps> = (props) => {
  const { variant, formula } = props;
  const { t } = useTranslation();

  const buildPrompt = useCallback((): string => {
    const codeFence = ['```', formula ?? '', '```'].join('\n');

    if (variant === 'debug') {
      const formattedError =
        props.error.line === -1 && props.error.column === -1
          ? props.error.message
          : `${props.error.line}:${props.error.column}: ${props.error.message}`;

      return [
        t('AI_ASSISTANT.FORMULA.DEBUG_PROMPT'),
        '',
        codeFence,
        '',
        t('AI_ASSISTANT.FORMULA.ERROR_HEADER'),
        formattedError,
        '',
      ].join('\n');
    }

    return [codeFence, '', t('AI_ASSISTANT.FORMULA.EXPLAIN_PROMPT')].join('\n');
  }, [formula, t, variant, props]);

  const handleActivate = useCallback(() => {
    // Always show assistant because useFlux cannot be accessed from the error panel.
    setAIAssistantDisplayed(true);
    setAIAssistantExternalPrompt({
      prompt: buildPrompt(),
      agent: 'formula',
    });
  }, [buildPrompt]);

  return variant === 'debug' ? (
    <Button
      variant="outline"
      label={t('AI_ASSISTANT.FORMULA.DEBUG_LABEL')}
      onClick={handleActivate}
      icon="fa-sparkles"
      iconStyle="color"
      iconColor="var(--tw-text-red-800)"
      iconPrefix="fa-solid"
      tooltip={t('AI_ASSISTANT.FORMULA.DEBUG_TOOLTIP')}
      testId="formulaAiAssistantDebug"
      extraClassNames="formula-debug-button tw:text-nowrap tw:border-sq-formula-error tw:border-1 tw:bg-inherit"
    />
  ) : (
    <Button
      variant="no-border"
      label={t('AI_ASSISTANT.FORMULA.EXPLAIN_LABEL')}
      onClick={handleActivate}
      icon="fa-sparkles"
      iconStyle="color"
      iconColor="var(--sq-color-dark)"
      iconPrefix="fa-solid"
      tooltip={t('AI_ASSISTANT.FORMULA.EXPLAIN_TOOLTIP')}
      testId="formulaAiAssistantExplain"
      extraClassNames="formula-explain-button tw:text-nowrap tw:p-0"
    />
  );
};
